import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Button, Drawer, Input, PartnerPicker } from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { useCreateReview } from '../api/reviewQueries'

export interface NewPriceReviewDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * "New review" drawer: pick the customer (and, if known, the date the new
 * prices take effect), then the review opens with one row per line on that
 * customer's Odoo pricelist. The date can also be set later, before approval.
 */
export function NewPriceReviewDrawer({ open, onOpenChange }: NewPriceReviewDrawerProps) {
  const navigate = useNavigate()
  const create = useCreateReview()
  const [partner, setPartner] = useState<PickerValue>(null)
  const [effective, setEffective] = useState('')

  function close() {
    setPartner(null)
    setEffective('')
    create.reset()
    onOpenChange(false)
  }

  function submit() {
    if (!partner) return
    create.mutate(
      { partner_id: partner[0], effective_date: effective || null },
      {
        onSuccess: (detail) => {
          if (detail.warnings?.length) toast.warning(detail.warnings.join(' '))
          close()
          void navigate({ to: '/pricelists/reviews/$reviewId', params: { reviewId: detail.review.id } } as never)
        },
      },
    )
  }

  return (
    <Drawer
      surface="form"
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      size="wide"
      title="New price review"
      meta="Pulls every line on the customer's current Odoo pricelist, with our cost, margin and order volume. Nothing changes in Odoo until an admin approves."
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={!partner || create.isPending}>
            {create.isPending ? 'Loading pricelist…' : 'Open review'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-role-section-label">Customer</span>
          <PartnerPicker
            kind="customer"
            variant="field"
            placeholder="Customer…"
            value={partner}
            onChange={setPartner}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-role-section-label">Effective date (optional)</span>
          <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
          <span className="text-role-metadata">The day the new prices start. You can set it later; it is required to approve.</span>
        </label>
        {create.isError ? <p className="m-0 text-sm text-danger">{(create.error as Error).message}</p> : null}
      </div>
    </Drawer>
  )
}
